import { IDeliveryClient } from '@kontent-ai/delivery-sdk';
import { AccessCodeMapping } from '../models';

/**
 * Cached access-code-to-visibility-group mappings, fetched from
 * the Kontent.AI content type "access_code_mapping".
 *
 * Each entry maps a login access code (lowercased) to its visibility groups.
 * When access codes change yearly, CMS editors update the `access_code`
 * text field — the item codename stays constant, and no code changes are needed.
 *
 * Retailer/dealer numbers (e.g. "123456") are NOT in this map.
 * This includes Retailers, Executive Managers, and General Managers
 * who all log in with a 6-digit retailer number instead of an access code.
 * They are handled by the API's `isDealer` flag — the code falls back
 * to "general" (confInfo/faq) and "dealer" (agenda) automatically.
 */

export type AccessCodeConfig = {
  confInfoGroup: string;      // confinfovisibility taxonomy value
  faqGroup: string;           // faqvisibility taxonomy value
  agendaVisibility: string;   // agendavisibility taxonomy value: 'dealer' | 'nondealer' | 'lpc' | 'all'
  hasReportingAccess: boolean;
  isDealerCode: boolean;
  roleName: string;           // role name for role-based item visibility (e.g. "Retailer", "Corporate")
};

// Per-client cache so NBC and ABC each fetch only once
const cache: Map<IDeliveryClient, Map<string, AccessCodeConfig>> = new Map();

/**
 * Fetches all access_code_mapping items from CMS (once per client)
 * and returns a Map<accessCode, AccessCodeConfig>.
 */
export const getAccessCodeMappings = async (
  client: IDeliveryClient
): Promise<Map<string, AccessCodeConfig>> => {
  // Return cached result if already fetched for this client
  if (cache.has(client)) {
    return cache.get(client)!;
  }

  const mappings = new Map<string, AccessCodeConfig>();

  try {
    const response = await client
      .items<AccessCodeMapping>()
      .type('access_code_mapping')
      .toPromise();

    for (const item of response.data.items) {
      const code = (item.elements.access_code.value ?? '').trim().toLowerCase();
      const confInfoTax = item.elements.conf_info_visibility?.value ?? [];
      const faqTax = item.elements.faq_visibility?.value ?? [];
      const agendaTax = item.elements.agenda_visibility?.value ?? [];
      const reportChoices = (item.elements.has_reporting_access?.value ?? []).map(
        (v: any) => v.codename
      );
      const dealerChoices = (item.elements.is_dealer_code?.value ?? []).map(
        (v: any) => v.codename
      );

      const roleNameTax = item.elements.role_name?.value ?? [];
      const roleName = roleNameTax.length > 0 ? roleNameTax[0].codename : '';

      mappings.set(code, {
        confInfoGroup: confInfoTax.length > 0 ? confInfoTax[0].codename : 'general',
        faqGroup: faqTax.length > 0 ? faqTax[0].codename : 'general',
        agendaVisibility: agendaTax.length > 0 ? agendaTax[0].codename : 'nondealer',
        hasReportingAccess: reportChoices.includes('yes'),
        isDealerCode: dealerChoices.includes('yes'),
        roleName,
      });
    }
  } catch {
    // On fetch failure, return empty map — callers fall back to defaults
    console.warn('Failed to fetch access_code_mapping from CMS; using defaults.');
  }

  cache.set(client, mappings);
  return mappings;
};

/** Lookup helpers with safe fallback defaults */
export const getConfInfoGroupForCode = (
  mappings: Map<string, AccessCodeConfig>,
  accessCode: string,
  isDealerFromApi: boolean = false
): string => {
  const config = mappings.get(accessCode.toLowerCase());
  if (config) return config.confInfoGroup;
  // 6-digit retailer numbers: use the empty-key fallback from CMS if available
  const fallback = mappings.get('');
  if (fallback) return fallback.confInfoGroup;
  return isDealerFromApi ? 'dealer' : 'general';
};

export const getFaqGroupForCode = (
  mappings: Map<string, AccessCodeConfig>,
  accessCode: string
): string => {
  return mappings.get(accessCode.toLowerCase())?.faqGroup ?? 'general';
};

/**
 * Returns agenda visibility for the given access code.
 * If the code is not in the CMS map (e.g. 6-digit retailer number used
 * by Retailers, Executive Managers, and General Managers), falls back
 * to isDealer from the API: true → 'dealer', false → 'nondealer'.
 */
export const getAgendaVisibilityForCode = (
  mappings: Map<string, AccessCodeConfig>,
  accessCode: string,
  isDealerFromApi: boolean
): string => {
  const config = mappings.get(accessCode.toLowerCase());
  if (config) return config.agendaVisibility;
  // Retailer numbers won't be in the map — use API's isDealer flag
  return isDealerFromApi ? 'dealer' : 'nondealer';
};

/**
 * Returns the role name for a given access code.
 * Retailers (6-digit dealer numbers not in the CMS map) default to 'Retailer'
 * when the API indicates they are a dealer.
 */
export const getRoleNameForCode = (
  mappings: Map<string, AccessCodeConfig>,
  accessCode: string,
  isDealerFromApi: boolean = false
): string => {
  const config = mappings.get(accessCode.toLowerCase());
  if (config?.roleName) return config.roleName;
  // 6-digit retailer numbers: use the empty-key fallback from CMS if available
  const fallback = mappings.get('');
  if (fallback?.roleName) return fallback.roleName;
  return isDealerFromApi ? 'retailer' : '';
};

/**
 * Determines if an item is visible to the user based on their role.
 * If visibleRoles is empty/null, item is visible to ALL users.
 * If user has no role, only items with empty roles are shown.
 */
export const isVisibleToUser = (
  visibleRoles: string[] | null | undefined,
  roleName: string | null | undefined
): boolean => {
  if (!visibleRoles || visibleRoles.length === 0) return true;
  if (!roleName) return false;

  return visibleRoles.some(
    role => role.toLowerCase() === roleName.toLowerCase()
  );
};
